/**
 * IDENTIDAD FISCAL de la empresa REAL — el bloque que faltaba en la cotización.
 *
 * 🔴 El defecto que cierra: en TODO `partnerPortal/src` no había un solo campo fiscal. El
 * asesor cotizaba, cobraba y entregaba una empresa sin NIT ni actividad económica, y el dueño
 * se enteraba en su primera venta con un «El emisor no tiene actividad económica» que no decía
 * dónde arreglarlo.
 *
 * Ojo con lo que NO es: la empresa de PRUEBA no necesita nada de esto — nace con la identidad
 * fiscal de su giro para que el prospecto pueda emitir desde el primer minuto. Estos datos son
 * los del cliente de verdad, y sin ellos «Pasar a producción» queda bloqueado.
 *
 * GEOGRAFÍA: solo se pide DEPARTAMENTO y DISTRITO. El municipio lo deriva el servidor (el mapa
 * inverso es determinista). Pedirlo aquí solo añadía una pareja que podía quedar incoherente,
 * y de hecho quedaba: los formularios guardaban el mismo código en ambos campos, así que toda
 * dirección que producían era de las que Hacienda rechaza en recepción.
 */

import { useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select'

const VACIO = {
  nit: '', nrc: '', nombre: '', nombreComercial: '',
  actividadEconomica: '', correo: '', telefono: '',
  direccion: { departamento: '', distrito: '', complemento: '' },
}

/** Cuántas actividades se listan al buscar. Son 774: pintarlas todas no ayuda a nadie. */
const MAX_RESULTADOS = 40

export default function IdentidadFiscal({ emisor, onChange, cats, cargando, pais }) {
  // La actividad CAT-019 y la geografía departamento/distrito son de HACIENDA de El Salvador.
  // Con otro país no se piden ni se validan contra ese catálogo. Sin país elegido (borrador
  // viejo) se conserva el formulario histórico.
  const esSV = !pais || pais === 'SV'
  const e = { ...VACIO, ...(emisor || {}), direccion: { ...VACIO.direccion, ...(emisor?.direccion || {}) } }
  const [busqueda, setBusqueda] = useState('')

  const set = (patch) => onChange({ ...e, ...patch })
  const setDir = (patch) => onChange({ ...e, direccion: { ...e.direccion, ...patch } })

  // Memoizado para que el `|| []` no cree un array nuevo en cada render y recalcule los 774
  // registros del catálogo con cada tecla.
  const actividades = useMemo(() => cats?.actividadEconomica || [], [cats])
  const actividadSel = useMemo(
    () => actividades.find((a) => a.codigo === e.actividadEconomica) || null,
    [actividades, e.actividadEconomica],
  )
  const resultados = useMemo(() => {
    const q = busqueda.trim().toLowerCase()
    if (!q) return []
    return actividades
      .filter((a) => a.codigo.includes(q) || a.valor.toLowerCase().includes(q))
      .slice(0, MAX_RESULTADOS)
  }, [actividades, busqueda])

  // Distritos del departamento elegido. El backend sirve `distritos` desde F3; si un despliegue
  // a medias todavía no los trae, el desplegable sale vacío y se dice por qué en vez de ofrecer
  // municipios que Hacienda rechazaría como distrito.
  const distritos = useMemo(
    () => (cats?.distritos || []).filter((d) => d.departamento === e.direccion.departamento),
    [cats, e.direccion.departamento],
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-h3 flex items-center gap-2">
          Identidad fiscal (empresa real)
          {actividadSel && e.nit ? <Badge variant="secondary">Completa</Badge> : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertDescription className="text-caption">
            Son los datos con los que el cliente facturará de verdad. <strong>La empresa de prueba
            no los necesita</strong>: nace con los del giro para que el dueño pueda emitir desde el
            primer día. Estos hacen falta para <strong>pasarla a producción</strong>.
          </AlertDescription>
        </Alert>

        {cargando && <p className="text-caption text-neutral-500 dark:text-neutral-400">Cargando catálogos del Ministerio de Hacienda…</p>}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>NIT</Label>
            <Input value={e.nit} placeholder="0614-123456-101-2"
              onChange={(ev) => set({ nit: ev.target.value })} />
          </div>
          <div>
            <Label>NRC</Label>
            <Input value={e.nrc} placeholder="123456-7"
              onChange={(ev) => set({ nrc: ev.target.value })} />
            <p className="text-caption text-neutral-500 dark:text-neutral-400 mt-1">
              Solo si está inscrito como contribuyente. Sin NRC emitirá recibos, no DTE.
            </p>
          </div>
          <div>
            <Label>Razón social</Label>
            <Input value={e.nombre} placeholder="Como aparece en el NIT"
              onChange={(ev) => set({ nombre: ev.target.value })} />
          </div>
          <div>
            <Label>Nombre comercial</Label>
            <Input value={e.nombreComercial} placeholder="Con el que lo conocen"
              onChange={(ev) => set({ nombreComercial: ev.target.value })} />
          </div>
        </div>

        {!esSV && (
          <Alert>
            <AlertDescription className="text-caption">
              La actividad económica (CAT-019) y el departamento/distrito son del Ministerio de
              Hacienda de El Salvador y no aplican a <strong>{pais}</strong>. Captura el
              identificador fiscal, la razón social, el correo y la dirección en texto libre.
            </AlertDescription>
          </Alert>
        )}

        {esSV && (
        <div>
          <Label>Actividad económica (CAT-019)</Label>
          {actividadSel ? (
            <div className="flex items-center gap-2 mt-1">
              <code className="font-mono text-caption bg-neutral-100 dark:bg-neutral-800 px-2 py-1 rounded">{actividadSel.codigo}</code>
              <span className="text-body-sm text-neutral-700 dark:text-neutral-300">{actividadSel.valor}</span>
              <button type="button" className="text-primary text-caption underline"
                onClick={() => { set({ actividadEconomica: '' }); setBusqueda('') }}>
                Cambiar
              </button>
            </div>
          ) : (
            <>
              <Input value={busqueda} placeholder="Busca por nombre o código: «restaurante», «56101»…"
                onChange={(ev) => setBusqueda(ev.target.value)} />
              {resultados.length > 0 && (
                <ul className="mt-1 max-h-56 overflow-y-auto rounded border border-neutral-200 dark:border-neutral-800 divide-y">
                  {resultados.map((a) => (
                    <li key={a.codigo}>
                      <button type="button"
                        className="w-full text-left px-3 py-2 hover:bg-neutral-50 dark:hover:bg-neutral-800/60 text-body-sm"
                        onClick={() => { set({ actividadEconomica: a.codigo }); setBusqueda('') }}>
                        <code className="font-mono text-caption text-neutral-500 dark:text-neutral-400 mr-2">{a.codigo}</code>
                        {a.valor}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {busqueda.trim() && resultados.length === 0 && (
                <p className="text-caption text-neutral-500 dark:text-neutral-400 mt-1">
                  Ninguna actividad del catálogo coincide. No se puede inventar un código: uno que no
                  existe pasa toda validación y lo rechaza Hacienda al recibir el documento.
                </p>
              )}
            </>
          )}
        </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          {esSV && (
          <div>
            <Label>Departamento</Label>
            <Select value={e.direccion.departamento || undefined}
              onValueChange={(v) => setDir({ departamento: v, distrito: '' })}>
              <SelectTrigger><SelectValue placeholder="Elige el departamento…" /></SelectTrigger>
              <SelectContent>
                {(cats?.departamentos || []).map((d) => (
                  <SelectItem key={d.codigo} value={d.codigo}>{d.valor}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          )}
          {esSV && (
          <div>
            <Label>Distrito</Label>
            <Select value={e.direccion.distrito || undefined} disabled={!e.direccion.departamento}
              onValueChange={(v) => setDir({ distrito: v })}>
              <SelectTrigger><SelectValue placeholder="Elige el distrito…" /></SelectTrigger>
              <SelectContent>
                {distritos.map((d) => (
                  <SelectItem key={`${d.departamento}-${d.codigo}`} value={d.codigo}>{d.valor}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-caption text-neutral-500 dark:text-neutral-400 mt-1">El municipio lo calcula el sistema.</p>
          </div>
          )}
          <div className="sm:col-span-2">
            <Label>Dirección</Label>
            <Input value={e.direccion.complemento} placeholder="Calle, número, colonia…"
              onChange={(ev) => setDir({ complemento: ev.target.value })} />
          </div>
          <div>
            <Label>Correo fiscal</Label>
            <Input type="email" value={e.correo} placeholder="facturacion@empresa.sv"
              onChange={(ev) => set({ correo: ev.target.value })} />
          </div>
          <div>
            <Label>Teléfono</Label>
            <Input type="tel" value={e.telefono} placeholder="2200-0000"
              onChange={(ev) => set({ telefono: ev.target.value })} />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
